import moment from "moment";
import { roundToDecimals } from "PFCore/helpers/rounding";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { convertToPercentage } from "PFCore/utilities/math";
import { TimeRuleRange } from "PFTypes/booking";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import {
  AvailabilityRangeCalendar,
  AvailabilityRangeCalendarRef
} from "../../../availability_range_calendar";
import { DEFAULT_MONTHS_FORWARD_LIMIT } from "../../../constants";
import { LoadEstimationData } from "../../../hooks/use_load_estimation";
import { InfoIcon } from "../../info_icon/info_icon";
import { SideInfo } from "../../parts/side_info";
import commonCss from "../../rules_carousel.module.scss";
import css from "./availability_with_hours.module.scss";
import { HoursPerDay } from "./hours_per_day";

type AvailabilityWithHoursProps = {
  range: TimeRuleRange;
  onChange: (range: Partial<TimeRuleRange>) => void;
  minDate?: { start: string; end?: string };
  errors?: Record<string, string>;
  portalRef?: React.RefObject<HTMLDivElement | null>;
  loadData: LoadEstimationData;
  isDisabled?: boolean;
};

export const AvailabilityWithHours = ({
  range: { start, end, day_of_week },
  onChange,
  errors,
  minDate,
  portalRef,
  loadData,
  isDisabled
}: AvailabilityWithHoursProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "availabilityRequirement" });
  const {
    data: { availability }
  } = useMyAccountInfo();
  const { formatISODate } = useDateFormatter();

  const setHours = (value: string, index: number) => {
    const valueInMinutes = roundToDecimals((+value.replace(",", ".") || 0) * 60);
    const newDayOfWeek = [...day_of_week!];
    newDayOfWeek[index] = Math.max(0, Math.min(valueInMinutes, 1440));
    onChange({ day_of_week: newDayOfWeek });
  };

  const rangeCalendarRef = useRef<AvailabilityRangeCalendarRef>(null);

  const matchMonthsForwardLimit = availability?.match_months_limit || DEFAULT_MONTHS_FORWARD_LIMIT;
  const matchMaxDate = formatISODate(moment().add(matchMonthsForwardLimit, "months").endOf("month"));
  const { totalAvailableHours, percentageLoad } = loadData;

  const handleDatesChange = (start: string, end: string) => {
    onChange({ start, end });
  };

  return (
    <div className={css.wrapper}>
      <span className={commonCss.row}>
        <AvailabilityRangeCalendar
          key={`${start}+${end}`}
          ref={rangeCalendarRef}
          // @ts-ignore
          start={start}
          end={end || undefined}
          matchMaxDate={matchMaxDate}
          onChange={handleDatesChange}
          errors={errors}
          wrapped
          minDate={minDate}
          portalRef={portalRef}
          isDisabled={isDisabled}
        />
        <SideInfo
          label={t("totalAvailable")}
          value={totalAvailableHours !== null ? t("nHours", { count: Math.round(totalAvailableHours) }) : "-"}
          tooltipContent={t("tooltips.totalAvailable")}
        />
      </span>
      <div className={css.hoursPerDayLabel}>{t("selectHoursPerDay")}</div>
      <span className={commonCss.row}>
        <HoursPerDay onHoursChange={setHours} dayOfWeek={day_of_week!} isDisabled={isDisabled} />
        <InfoIcon isTimeRule />
        <SideInfo
          label={t("requiredPercent")}
          value={convertToPercentage(percentageLoad, { isNormalized: true })}
          tooltipContent={t("tooltips.requiredPercent")}
        />
      </span>
    </div>
  );
};
